<template>
  <div ref="rootRef" class="ai-chat-panel-bridge"></div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { unmountComponentAtNode } from 'react-dom';
import { render } from 'react-dom';
import React from 'react';
import Wrapper from '@/react-app/components/Wrapper';
import AiChatPanel from '@/react-app/components/ai-chat-panel';
import router from '@/router';
import type {
  AiChatMessage,
  AiChatConsultFn,
  AiChatConsultExtraParamsGetter,
  AiChatConsultResult,
  AiChatContext,
} from '@/store/modules/aiChat';
import store from '@/store';
import { i18n } from '@/i18n';
import { consultDynamicWaveRuleWithPolling } from '@/api/rule-center/dynamic-rule';
import { getAiChatBusinessDomain } from '@/utils/ai-chat/business-domain';

const buildConsultFn = (
  getExtraParams?: AiChatConsultExtraParamsGetter | null
): AiChatConsultFn => {
  return async (
    userPrompt: string,
    sessionId: string,
    signal?: AbortSignal
  ): Promise<AiChatConsultResult> => {
    const businessDomain = getAiChatBusinessDomain({
      routePath: String(router.currentRoute.path || ''),
    });
    const res = await consultDynamicWaveRuleWithPolling(
      {
        user_prompt: userPrompt,
        session_id: sessionId || undefined,
        ...(getExtraParams?.(userPrompt, sessionId) ?? {}),
        ...(businessDomain ? { business_domain: businessDomain } : {}),
      },
      {
        signal,
      }
    );
    return {
      answer: res?.answer || i18n('No Data'),
      session_id: res?.session_id || sessionId,
      task_id: res?.task_id,
      dialogue_status: res?.status,
      feedback_status: res?.feedback_status,
    };
  };
};

export default defineComponent({
  name: 'AiChatPanelBridge',
  setup() {
    const rootRef = ref<HTMLElement | null>(null);
    const getAiChatState = () => store.state.aiChat;

    const renderComponent = () => {
      if (!rootRef.value) return;
      const state = getAiChatState();
      const ctx = state.chatContext;
      const messages = (ctx?.messages ?? []) as AiChatMessage[];
      const sessionId = ctx?.sessionId ?? '';
      const initialLoading = ctx?.loading ?? false;
      const title = ctx?.title;
      const contextLabel = ctx?.contextLabel;
      const contextAnswer = ctx?.contextAnswer;
      const suggestions = ctx?.suggestions ?? [];
      const consultFn =
        ctx?.consultFn ??
        state.registeredConsultFn ??
        buildConsultFn(
          ctx?.consultExtraParamsGetter ??
            state.registeredConsultExtraParamsGetter
        );
      const userInfo = (store.state as any).user?.userInfo ?? {};
      const userName = userInfo.name;

      render(
        React.createElement(
          Wrapper,
          null,
          React.createElement(AiChatPanel, {
            messages,
            sessionId,
            initialLoading,
            title,
            contextLabel,
            contextAnswer,
            userName,
            suggestions,
            consultFn,
            onClose: () => store.commit('closeAiChat'),
            onMessagesChange: (
              updatedMessages: AiChatMessage[],
              updatedSessionId: string
            ) => {
              const currentCtx = getAiChatState().chatContext;
              store.commit('setAiChatContext', {
                ...(currentCtx ?? {}),
                messages: updatedMessages,
                sessionId: updatedSessionId,
                loading: false,
              } as AiChatContext);
            },
            onSuggestionsChange: (updatedSuggestions: string[]) => {
              const currentCtx = getAiChatState().chatContext;
              store.commit('setAiChatContext', {
                ...(currentCtx ?? {}),
                suggestions: updatedSuggestions,
              } as AiChatContext);
            },
          })
        ),
        rootRef.value
      );
    };

    onMounted(() => {
      renderComponent();
    });

    watch(
      () => getAiChatState().chatContext,
      () => {
        renderComponent();
      },
      { deep: true }
    );

    watch(
      () => getAiChatState().registeredConsultFn,
      () => {
        renderComponent();
      }
    );

    watch(
      () => getAiChatState().registeredConsultExtraParamsGetter,
      () => {
        renderComponent();
      }
    );

    onBeforeUnmount(() => {
      if (rootRef.value) {
        unmountComponentAtNode(rootRef.value);
      }
    });

    return {
      rootRef,
    };
  },
});
</script>

<style scoped>
.ai-chat-panel-bridge {
  height: 100%;
}
</style>
